import { Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, Output, ViewChild } from '@angular/core';
import { ChartLibraryService } from '@services/chart-library.service';
import { EventDelegatorService } from '@services/event-delegator.service';
import { ToastrService } from '@services/toaster.service';
import { OpenSidebarData, SidebarData } from '@pages/mattermaps/components/layers/models/OpenSideBarData';
import type * as leaflet from 'leaflet';

declare const L;

@Component({
  selector: 'app-route-map',
  standalone: true,
  templateUrl: './route-map.component.html',
  styleUrls: ['./route-map.component.scss'],
})
export class RouteMapComponent implements OnChanges {
  @ViewChild('map', { static: true }) mapElement: ElementRef;
  @Input() data: any;
  @Input() networkgraphdata: any;
  @Output() getPinInfo = new EventEmitter<any>();
  @Output() openSidebar = new EventEmitter<OpenSidebarData>();

  private map: leaflet.Map;
  private links: any;
  private latlongDict = {};
  private nodeDict = {};
  private legend: any;
  private pathControl: any;
  private nodesLocDict = {};
  private markersList: any = [];
  private pathList: any = [];
  private highlightedMarkers: any = [];
  private categories = [];

  get isNewDashboard() {
    return this.data?.isfrom === 'newDashboard';
  }

  get tileSize() {
    return this.isNewDashboard ? 550 : 512;
  }

  get mapZoom() {
    return this.isNewDashboard ? 2 : 3;
  }

  constructor(
    private toastr: ToastrService,
    private eventDelegatorService: EventDelegatorService,
    private chartService: ChartLibraryService,
    private zone: NgZone
  ) {}

  ngOnChanges() {
    if (!this.networkgraphdata) {
      return;
    }

    this.zone.runOutsideAngular(() => {
          this.clearMap();
          this.initMap(this.mapElement.nativeElement);
          this.drawLayers();
    });

    this.toastr.show('Please click a node to see the path');
  }

  private drawLayers(): void {
    this.createDict();
    this.addMarkers();
    this.links = [];
    this.checkLinks([0]);
    this.addLinks();
    this.addLegends();
    this.addClearPathControl();
  }

  private initMap(el: HTMLElement): void {
    const mapOptions: leaflet.MapOptions = {
      maxBoundsViscosity: 1.0,
      preferCanvas: true,
      attributionControl: false,
    };

    this.map = L.map(el, mapOptions).setView([15.395158, 19.062826], this.mapZoom)
      .whenReady(() => {
        setTimeout(() => {
          this.map.invalidateSize();
        }, 100);
      });

    const tileOptions = {
      id: 'mapbox/light-v9',
      tileSize: this.tileSize,
      maxZoom: 18,
      zoomOffset: -1,
      minZoom: 1,
      noWrap: true,
      bounds: [[-90, -180], [90, 180]],
    };

    const tiles = this.chartService.getMapTiles(L, tileOptions);
    tiles.addTo(this.map);

    this.map.on('click', () => {
      this.clearMarkers();
      this.addMarkers();
    });
  }

  private createDict(): void {
    const nodeDict = {};
    this.nodesLocDict = {};
    this.networkgraphdata.nodes.forEach((node) => {
      const rid = node?.rid?.replace('#', '');

      if (rid && !nodeDict[rid]) nodeDict[rid] = node;

      this.nodesLocDict[node.id] = node;
      if (node.lat && node.lon) {
        this.nodesLocDict[node.id].geoLoc = {
          lat: node.lat,
          long: node.lon,
          tier: node.level
        };
        this.nodesLocDict[node.id].latlong = [node.lat, node.lon];
      }
    });

    this.networkgraphdata.pinmap.features.forEach((feat) => {
      // use 0,0 as lat long if not provided in API
      const latLong = [feat.geometry.coordinates[0] || 0, feat.geometry.coordinates[1] || 0];

      nodeDict[feat.id].geoLoc = {
        lat: latLong[0],
        long: latLong[1],
        tier: feat.properties.tier
      };
      nodeDict[feat.id].latlong = latLong;
    });
    this.nodeDict = nodeDict;

    const latlongDict = {};
    const { nodes } = this.networkgraphdata;
    for (const node of nodes) {
      if (!node.latlong) node.latlong = [0, 0];

      const popup = L.DomUtil.create('div', 'marker-info');
      if (!latlongDict[node.latlong]) {
        popup.innerHTML = `<ul class="site-list"><li data-rid='${node.rid}' class="marker-li tier-${node?.geoLoc?.tier}">${node.name}</li></ul>`;
        latlongDict[node.latlong] = {
          nodes: [node], // assign as first value of array
          nodeIds: [node.id], // assign as first value of array
          rids: [node.rid], // assign as first value of array
          data: popup, // list of node names in popup
          tier: node?.geoLoc?.tier, // force color of marker same as first node
          text: popup.innerHTML // keep innerHTML as string
        };
      } else if (latlongDict[node.latlong].rids.includes(node.rid)) {
        latlongDict[node.latlong].nodeIds.push(node.id);
      } else {
        latlongDict[node.latlong].nodes.push(node);
        latlongDict[node.latlong].nodeIds.push(node.id);
        latlongDict[node.latlong].rids.push(node.rid);
        popup.innerHTML = latlongDict[node.latlong].text.replace('</ul>', `<li data-rid='${node.rid}' class="marker-li tier-${node?.geoLoc?.tier}">${node.name}</li></ul>`);
        latlongDict[node.latlong].data = popup;
        latlongDict[node.latlong].text = popup.innerHTML;
      }

      popup.onclick = (ev) => {
        const { target } = ev;
        let rid = target.dataset?.rid;
        if (rid) {
          rid = rid.replace('#', '');
          this.emitOpenSidebar({
            rid: this.nodeDict[rid].rid,
            name: this.nodeDict[rid].name,
            tier: this.nodeDict[rid]?.geoLoc?.tier
          });
        }
      };
    }

    this.latlongDict = latlongDict;
  }

  private addMarkers(): void {
    Object.entries(this.latlongDict).forEach(([key, val]: [any, any]) => {
      const geo = L.latLng(key.split(',').map((v) => +v));
      const marker = L.marker(geo, {
        icon: this.getIcon(val.tier)
      })
        .bindPopup(val.data, { offset: L.point(0, 14) })
        .addTo(this.map)
        .on('click', () => {
          this.links = [];
          val.nodes.forEach((node) => {
            for (const i in node.parents) {
              if (!this.highlightedMarkers.includes(node.parents[i])) this.highlightedMarkers.push(node.parents[i]);
              if (!node.parents[+i + 1]) break;
              this.links.push({ target: node.parents[i], source: node.parents[+i + 1] });
            }
          });
          this.highlightPaths();
          this.addLinks();
          this.emitOpenSidebar({
            rid: val.nodes[0].rid,
            name: val.nodes[0].name,
            tier: val.nodes[0]?.geoLoc?.tier
          });
        });
      marker.properties = { nodeIds: val.nodeIds };
      this.markersList.push(marker);

      // Add tooltip only if multiple markers on same location
      if (val.nodes.length > 1) {
        marker.bindTooltip(`${val.nodes.length}`, { offset: L.point(10, -20) });
      }
    });
  }

  private getIcon(tier = 0): leaflet.Icon {
    const icons = {
      0: './assets/markers/marker-icon-2x-black.png',
      1: './assets/markers/marker-icon-2x-blue.png',
      2: './assets/markers/marker-icon-2x-red.png',
      3: './assets/markers/marker-icon-2x-green.png',
      4: './assets/markers/marker-icon-2x-orange.png',
      5: './assets/markers/marker-icon-2x-yellow.png',
      6: './assets/markers/marker-icon-2x-violet.png',
      7: './assets/markers/marker-icon-2x-pink.png',
      8: './assets/markers/marker-icon-2x-dblue.png',
      9: './assets/markers/marker-icon-2x-darkgreen.png',
      10: './assets/markers/marker-icon-2x-cyan.png',
      11: './assets/markers/marker-icon-2x-brown.png'
    };
    const icon = icons[tier] || icons[11];

    if (!this.categories.includes(tier)) {
      this.categories.push(tier);
    }

    return new L.Icon({
      iconUrl: icon,
      shadowUrl: './assets/markers/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });
  }

  private clearMap(): void {
    this.map?.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        layer.remove();
      }
    });
  }

  private clearMarkers(): void {
    this.markersList.forEach((marker) => {
      this.map.removeLayer(marker);
    });

    this.markersList = [];
  }

  private highlightPaths(): void {
    this.markersList.forEach((marker) => {
      const shouldHighlight = marker.properties.nodeIds.some((m) => this.highlightedMarkers.includes(m));
      if (!shouldHighlight) {
        this.map.removeLayer(marker);
      }
    });
  }

  private checkLinks(targets): void {
    this.links = this.networkgraphdata.links.filter((ln) => targets.includes(ln.source) || targets.includes(ln.target));
  }

  private addLinks(): void {
    this.links.forEach((link) => {
      const [latlng1, midpointLatLng, latlng2] = this.getCurveGeolocation(this.nodesLocDict[link.source].latlong, this.nodesLocDict[link.target].latlong);

      const pathOptions = {
        color: '#919191',
        weight: 2,
        animate: 500
      };

      const curveRoute = L.curve([
        'M', latlng1,
        'Q', midpointLatLng,
        latlng2
      ], pathOptions)
        .addTo(this.map);
      this.pathList.push(curveRoute);

      // get midpoint of the curved path
      const [center] = curveRoute.trace([0.5]);
      // get angle of the arrow
      const angle = this.getAngle(latlng1, latlng2, -1);
      // create arrowIcon
      const icon = L.divIcon({ className: 'arrow-icon', bgPos: [5, 5], html: `<div style="transform: rotate(${angle.toString()}deg)">▶</div>` });
      setTimeout(() => {
        // add arrowIcon
        const arrow = L.featureGroup([L.marker(center, { icon })])
          .bindTooltip(`${this.nodesLocDict[link.source].name} → ${this.nodesLocDict[link.target].name}`, { offset: L.point(0, 14), className: 'marker-li' })
          .addTo(this.map);

        this.pathList.push(arrow);
      }, 500);
    });
  }

  private addLegends(): void {
    if (this.legend) {
      this.map.removeControl(this.legend);
      this.legend = null;
    }

    const legend = new L.Control({ position: 'bottomleft' });

    legend.onAdd = () => {
      const div = L.DomUtil.create('div', 'infolegend legend');
      const labels = ['<strong>Tier</strong>'];
      const colorList = ['black', 'blue', 'red', 'green', 'orange', 'yellow', 'darkviolet', 'pink', 'darkblue', 'darkgreen', 'cyan', 'brown'];

      this.categories.sort((a, b) => a - b).forEach((cat) => {
        labels.push(
          `<span data-tier=${cat}><i style="background:${colorList[cat]}"></i> Tier ${cat}</span>`
        );
      });

      div.innerHTML = labels.join('<br>');

      return div;
    };

    legend.addTo(this.map);

    this.legend = legend;
  }

  private addClearPathControl(): void {
    if (this.pathControl) {
      this.map.removeControl(this.pathControl);
      this.pathControl = null;
    }

    const layerControl = new L.Control({ position: 'topleft' });

    layerControl.onAdd = () => {
      const div = L.DomUtil.create('div', 'info layers-control');

      div.innerHTML = '<div><img src="assets/images/hide-layers.png" class="hide-layer-icon"><span>&nbsp;Clear routes</span></div>';

      div.querySelector('div').addEventListener('click', () => {
        this.clearLinks();
      });

      return div;
    };

    layerControl.addTo(this.map);

    this.pathControl = layerControl;
  }

  private clearLinks(): void {
    this.pathList.forEach((marker) => {
      this.map.removeLayer(marker);
    });

    this.pathList = [];
  }

  private getCurveGeolocation(latlng1 = [0, 0], latlng2 = [0, 0]): [number[], number[], number[]] {
    const offsetX = latlng2[1] - latlng1[1];
    const offsetY = latlng2[0] - latlng1[0];

    const r = Math.sqrt(offsetX ** 2 + offsetY ** 2);
    const theta = Math.atan2(offsetY, offsetX);

    const thetaOffset = (3.14 / 8);

    const r2 = (r / 2) / (Math.cos(thetaOffset));
    const theta2 = theta + thetaOffset;

    const midpointX = (r2 * Math.cos(theta2)) + +latlng1[1];
    const midpointY = (r2 * Math.sin(theta2)) + +latlng1[0];

    const midpointLatLng = [midpointY, midpointX];

    return [latlng1, midpointLatLng, latlng2];
  }

  private getAngle(latLng1, latlng2, coef): string {
    const dy = latlng2[0] - latLng1[0];
    const dx = Math.cos((Math.PI / 180) * latLng1[0]) * (latlng2[1] - latLng1[1]);
    const ang = ((Math.atan2(dy, dx) / Math.PI) * 180 * coef);

    return (ang).toFixed(2);
  }

  private emitOpenSidebar(data: SidebarData): void {
    this.zone.runTask(() => {
      this.openSidebar.emit({ type: 'pinmap-events', data, controller: window.location.pathname });
    });
  }
}
